import {
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@App/contexts/Application';
import { containsAnySubStr } from '@Shared/helpers/string';
import { WaitViewLoader } from '@Views/wait/WaitViewLoader';
import { AuthContext } from '@src/app/contexts/Auth';
import { DebtConContext } from '@src/app/contexts/DebtCon';
import { useTrackStartFactFind } from '@src/app/hooks/useTrackStartFactFind';
import { ApplicationStage } from '@src/app/interfaces/application-stage.enum';
import { CreditCommitmentsData } from '@src/app/interfaces/debtcon';
import { validateIncomings } from '@src/app/utils/application';

import EligibilityLayout from '../layouts/EligibilityLayout';
import DashboardView from '../views/eligibility/DashboardView';

const Dashboard = () => {
    const { loggedIn } = useContext(AuthContext);

    const {
        loading,
        dashboardItemStatuses,
        applicationStage,
        applicationEmail,
        applicationId,
        callDateTime,
        debtConUser,
        loanDetails,
        isApplicationUpdated,
        needsAndPreferencesCompleted,
        deferredApplicant2TestUser,
        incomings,
        secondApplicantDeferred,
    } = useContext(ApplicationContext);

    const isDeferredCoApplicantPending =
        deferredApplicant2TestUser === true &&
        secondApplicantDeferred &&
        !validateIncomings(incomings?.[1]);

    const { fetchCreditCommitments, loading: debtConLoading } =
        useContext(DebtConContext);

    const [showWaitScreen, setShowWaitScreen] = useState<boolean>(false);

    const saveRef = useRef<any>(null);
    const didFetchRef = useRef(false);

    const navigate = useNavigate();

    const saveForLater = () => {
        return true;
    };

    useImperativeHandle(saveRef, () => ({
        saveMethod: saveForLater,
    }));

    useEffect(() => {
        if (window.hj && applicationEmail) {
            window.hj('identify', applicationEmail, {
                applicationId: applicationId,
            });
        }
    }, [applicationEmail, applicationId]);

    useTrackStartFactFind();

    //navigate to debt-con or loan-preferences
    useEffect(() => {
        const handleNavigation = async () => {
            const path = '/eligibility/debt-con';

            if (isDeferredCoApplicantPending) {
                setShowWaitScreen(false);
                return;
            }

            if (loggedIn && dashboardItemStatuses[4].completed) {
                if (!dashboardItemStatuses[5].completed) {
                    if (debtConUser === null) {
                        return;
                    }

                    if (!debtConUser) {
                        setShowWaitScreen(true);
                        return;
                    }

                    if (debtConUser) {
                        // prevents endpoint from being called multiple times
                        if (didFetchRef.current) return;
                        didFetchRef.current = true;

                        const response = await fetchCreditCommitments();

                        if (!response.success) {
                            return navigate(`${path}/intro`);
                        }

                        if (!response.data.length) {
                            return;
                        }

                        if (!loanDetails.debtConMainGoal) {
                            return navigate(`${path}/main-goal`);
                        }

                        const debtsNotComplete = response.data.some(
                            (debt: CreditCommitmentsData) =>
                                !debt.lenderName &&
                                !debt.dffDebtConSkipped &&
                                !debt.ignore,
                        );

                        navigate(
                            `${path}/${debtsNotComplete ? 'borrow' : 'review'}`,
                        );
                    }
                }

                if (
                    !dashboardItemStatuses[6].completed &&
                    needsAndPreferencesCompleted === false &&
                    isApplicationUpdated &&
                    applicationStage === ApplicationStage.DECISIONING_ACCEPT
                ) {
                    return navigate('/eligibility/loan-preferences');
                }
            }
        };
        handleNavigation();
    }, [
        dashboardItemStatuses,
        loggedIn,
        navigate,
        fetchCreditCommitments,
        debtConUser,
        loanDetails,
        isApplicationUpdated,
        needsAndPreferencesCompleted,
        applicationStage,
        isDeferredCoApplicantPending,
        incomings,
    ]);

    // navigate to offer
    useEffect(() => {
        const handleNavigation = async () => {
            const isRejected = containsAnySubStr(applicationStage || '', [
                ApplicationStage.DECISIONING_DECLINE,
                ApplicationStage.CLOSED,
            ]);

            if (
                loggedIn &&
                !isRejected &&
                dashboardItemStatuses[7]?.completed
            ) {
                const path = callDateTime ? 'confirmation' : 'explore';

                navigate(`/eligibility/offer/${path}`);
            }
        };

        handleNavigation();
    }, [
        dashboardItemStatuses,
        loggedIn,
        callDateTime,
        needsAndPreferencesCompleted,
        navigate,
        applicationStage,
    ]);

    if (showWaitScreen) {
        return (
            <EligibilityLayout
                className='h-screen'
                loading={false}
                ref={saveRef}
            >
                <WaitViewLoader loadingStopped />
            </EligibilityLayout>
        );
    }

    return (
        <EligibilityLayout
            className='h-screen'
            loading={loading || debtConLoading}
            ref={saveRef}
        >
            <DashboardView />
        </EligibilityLayout>
    );
};

export default Dashboard;
